import React, { useState, useEffect } from 'react';
import { Table, Button, Badge, Form, Modal, Row, Col, Card, Alert, Spinner } from 'react-bootstrap';

// Ensure API_URL is properly formatted
const API_URL = process.env.REACT_APP_API_URL || '';

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState('all');
    const [successMessage, setSuccessMessage] = useState('');
    const [activeRoleTab, setActiveRoleTab] = useState('app'); // 'app' or 'admin'
    const [updateLoading, setUpdateLoading] = useState(false);
    const [updateError, setUpdateError] = useState(null);
    const [selectedRole, setSelectedRole] = useState('');

    // Application user roles (from user_type enum)
    const appRoles = {
        STAFF: 'staff',
        STUDENT: 'student',
        PROF: 'prof'
    };

    // Administrative roles (from admin_role enum)
    const adminRoles = {
        SUPER_ADMIN: 'super_admin',
        ADMIN: 'admin',
        MODERATOR: 'moderator',
        ANALYST: 'analyst',
        SUPPORT: 'support'
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // Helper function to build proper URLs
    const buildApiUrl = (path) => {
        // Ensure API_URL doesn't have trailing slash and path has leading slash
        const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
        const formattedPath = path.startsWith('/') ? path : `/${path}`;
        return `${baseUrl}${formattedPath}`;
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const url = buildApiUrl('/admin/users');
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error('You do not have access to view user management. Please contact an administrator for assistance.');
                }
                throw new Error('Failed to fetch users');
            }

            const data = await response.json();
            setUsers(data);
            setError(null);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateAppRole = async () => {
        if (!selectedRole) {
            setUpdateError("Please select a role");
            return;
        }

        try {
            setUpdateLoading(true);
            setUpdateError(null);
            
            const url = buildApiUrl(`/admin/users/${selectedUser.user_id}/user-role`);
            
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ role: selectedRole })
            });

            // Check for HTML response (error)
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.indexOf("text/html") !== -1) {
                throw new Error(`Received HTML response. API endpoint might not be available at: ${url}`);
            }

            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error('You do not have permission to update user roles. Please contact an administrator for assistance.');
                }
                
                try {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Failed to update application role');
                } catch (jsonError) {
                    throw new Error('Failed to update application role');
                }
            }

            await response.json();
            
            // Refresh user data after successful update
            await fetchUsers();
            
            setSuccessMessage(`Successfully updated application role to ${selectedRole}`);
            setTimeout(() => setSuccessMessage(''), 3000);
            setShowModal(false);
            setSelectedRole('');
        } catch (err) {
            console.error('Error updating application role:', err);
            setUpdateError(err.message || 'Failed to update role');
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleUpdateAdminRole = async () => {
        if (!selectedRole) {
            setUpdateError("Please select a role");
            return;
        }

        try {
            setUpdateLoading(true);
            setUpdateError(null);
            
            const url = buildApiUrl(`/admin/users/${selectedUser.user_id}/admin-role`);
            
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ role: selectedRole })
            });
            
            // Check for HTML response (error)
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.indexOf("text/html") !== -1) {
                throw new Error(`Received HTML response. API endpoint might not be available at: ${url}`);
            }

            // Fallback for potential development issues - display a more helpful error
            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error('You do not have permission to update administrative roles. Please contact a super administrator for assistance.');
                }
                
                // Try to parse as JSON first
                try {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Failed to update administrative role');
                } catch (jsonError) {
                    // If JSON parsing fails, provide a more descriptive error
                    throw new Error(`Server returned ${response.status}: The admin-role endpoint may not be implemented at: ${url}`);
                }
            }
            
            // Try parsing response
            try {
                await response.json();
            } catch (error) {
                console.warn("Response wasn't valid JSON, but request may have succeeded");
            }
            
            // Refresh user data after request
            await fetchUsers();
            
            setSuccessMessage(`Administrative role update request sent for: ${formatRoleName(selectedRole)}`);
            setTimeout(() => setSuccessMessage(''), 3000);
            setShowModal(false);
            setSelectedRole('');
        } catch (err) {
            console.error('Error updating administrative role:', err);
            setUpdateError(err.message || 'Failed to update role');
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleRemoveAdminRole = async () => {
        try {
            setUpdateLoading(true);
            setUpdateError(null);
            
            const url = buildApiUrl(`/admin/users/${selectedUser.user_id}/admin-role`);
            
            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            
            // Check for HTML response
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.indexOf("text/html") !== -1) {
                throw new Error(`Received HTML response. API endpoint might not be available at: ${url}`);
            }

            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error('You do not have permission to remove administrative roles. Please contact a super administrator for assistance.');
                }
                
                try {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Failed to remove administrative role');
                } catch (jsonError) {
                    throw new Error(`Server returned ${response.status}: The admin-role endpoint may not be implemented at: ${url}`);
                }
            }

            try {
                await response.json();
            } catch (error) {
                console.warn("Response wasn't valid JSON, but request may have succeeded");
            }
            
            // Refresh user data
            await fetchUsers();
            
            setSuccessMessage(`Administrative role removal request sent`);
            setTimeout(() => setSuccessMessage(''), 3000);
            setShowModal(false);
        } catch (err) {
            console.error('Error removing administrative role:', err);
            setUpdateError(err.message || 'Failed to remove role');
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleUserAction = (user) => {
        setSelectedUser(user);
        setActiveRoleTab('app');
        setUpdateError(null);
        setSelectedRole('');
        setShowModal(true);
    };

    const getAppRoleBadgeVariant = (role) => {
        switch (role) {
            case appRoles.STAFF:
                return 'info';
            case appRoles.PROF:
                return 'success';
            case appRoles.STUDENT:
                return 'primary';
            default:
                return 'secondary';
        }
    };

    const getAdminRoleBadgeVariant = (role) => {
        switch (role) {
            case adminRoles.SUPER_ADMIN:
                return 'danger';
            case adminRoles.ADMIN:
                return 'warning';
            case adminRoles.MODERATOR:
                return 'info';
            case adminRoles.ANALYST:
                return 'success';
            case adminRoles.SUPPORT:
                return 'primary';
            default:
                return 'secondary';
        }
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = (
            user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        const matchesFilter = filter === 'all' || 
            user.user_type === filter || 
            user.admin_role === filter;

        return matchesSearch && matchesFilter;
    });

    const formatDate = (dateString) => {
        return dateString ? new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        }) : 'Never';
    };

    const formatRoleName = (role) => {
        return role ? role.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ') : 'None';
    };

    if (loading) return (
        <div className="text-center p-5" style={{ color: 'var(--text-primary)' }}>
            <Spinner animation="border" role="status" style={{ color: 'var(--text-primary)' }} />
            <h3 className="mt-3">Loading users...</h3>
        </div>
    );

    if (error) return (
        <Alert variant="danger" style={{ backgroundColor: 'rgba(220, 53, 69, 0.2)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
            <Alert.Heading>Error loading users</Alert.Heading>
            <p>{error}</p>
            <Button 
                variant="outline-danger" 
                onClick={fetchUsers}
                style={{ borderColor: '#dc3545', color: '#dc3545' }}
            >
                Try Again
            </Button>
        </Alert>
    );

    return (
        <div>
            <h2 className="mb-4" style={{ color: 'var(--text-primary)' }}>Users Management</h2>

            <div className="mb-3 small text-muted">
                API URL: {API_URL || 'Not set - check your environment variables'}
            </div>

            {successMessage && (
                <Alert variant="success" className="mb-3" style={{ backgroundColor: 'rgba(40, 167, 69, 0.2)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
                    {successMessage}
                </Alert>
            )}

            <Row className="mb-4">
                <Col md={4}>
                    <Form.Group>
                        <Form.Control
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ 
                                backgroundColor: 'var(--input-background)', 
                                color: 'var(--text-primary)',
                                borderColor: 'var(--border-color)'
                            }}
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        style={{ 
                            backgroundColor: 'var(--input-background)', 
                            color: 'var(--text-primary)',
                            borderColor: 'var(--border-color)'
                        }}
                    >
                        <option value="all">All Users</option>
                        <optgroup label="Application Roles">
                            {Object.values(appRoles).map(role => (
                                <option key={role} value={role}>
                                    {role.charAt(0).toUpperCase() + role.slice(1)}
                                </option>
                            ))}
                        </optgroup>
                        <optgroup label="Administrative Roles">
                            {Object.values(adminRoles).map(role => (
                                <option key={role} value={role}>
                                    {formatRoleName(role)}
                                </option>
                            ))}
                        </optgroup>
                    </Form.Select>
                </Col>
            </Row>

            <Card style={{ backgroundColor: 'var(--card-background)', color: 'var(--text-primary)', boxShadow: '0 4px 8px var(--shadow-color)' }}>
                <Card.Body>
                    <Table responsive>
                        <thead>
                            <tr style={{ color: 'var(--text-primary)' }}>
                                <th>Username</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Roles</th>
                                <th>Registration Date</th>
                                <th>Last Login</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center" style={{ color: 'var(--text-primary)' }}>
                                        No users found matching your criteria
                                    </td>
                                </tr>
                            ) : (
                                filteredUsers.map(user => (
                                    <tr key={user.user_id} style={{ color: 'var(--text-primary)' }}>
                                        <td>{user.username}</td>
                                        <td>{`${user.first_name} ${user.last_name}`}</td>
                                        <td>{user.email}</td>
                                        <td>
                                            <div className="d-flex flex-column gap-1">
                                                <Badge bg={getAppRoleBadgeVariant(user.user_type)}>
                                                    {`App: ${user.user_type || 'None'}`}
                                                </Badge>
                                                {user.admin_role && (
                                                    <Badge bg={getAdminRoleBadgeVariant(user.admin_role)}>
                                                        {`Admin: ${formatRoleName(user.admin_role)}`}
                                                    </Badge>
                                                )}
                                            </div>
                                        </td>
                                        <td>{formatDate(user.regdate)}</td>
                                        <td>{formatDate(user.last_login_date)}</td>
                                        <td>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => handleUserAction(user)}
                                                style={{ borderColor: 'var(--text-primary)', color: 'var(--text-primary)' }}
                                            >
                                                Manage Roles
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <Modal 
                show={showModal} 
                onHide={() => setShowModal(false)}
                backdrop="static"
                centered
                style={{ color: 'var(--text-primary)' }}
            >
                <Modal.Header style={{ backgroundColor: 'var(--card-background)', borderColor: 'var(--border-color)' }}>
                    <Modal.Title>Manage User Roles</Modal.Title>
                    <Button 
                        variant="link" 
                        onClick={() => setShowModal(false)}
                        style={{ color: 'var(--text-primary)' }}
                        disabled={updateLoading}
                    >
                        ×
                    </Button>
                </Modal.Header>
                <Modal.Body style={{ backgroundColor: 'var(--card-background)' }}>
                    {selectedUser && (
                        <>
                            <p><strong>User:</strong> {selectedUser.username}</p>
                            
                            {updateError && (
                                <Alert variant="danger" className="mb-3" style={{ backgroundColor: 'rgba(220, 53, 69, 0.2)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
                                    {updateError}
                                </Alert>
                            )}
                            
                            <div className="mb-3">
                                <Button
                                    variant={activeRoleTab === 'app' ? 'primary' : 'outline-primary'}
                                    className="me-2"
                                    onClick={() => {
                                        setActiveRoleTab('app');
                                        setSelectedRole('');
                                        setUpdateError(null);
                                    }}
                                    disabled={updateLoading}
                                >
                                    Application Role
                                </Button>
                                <Button
                                    variant={activeRoleTab === 'admin' ? 'primary' : 'outline-primary'}
                                    onClick={() => {
                                        setActiveRoleTab('admin');
                                        setSelectedRole('');
                                        setUpdateError(null);
                                    }}
                                    disabled={updateLoading}
                                >
                                    Administrative Role
                                </Button>
                            </div>

                            {activeRoleTab === 'app' ? (
                                <Card style={{ backgroundColor: 'var(--background-secondary)', borderColor: 'var(--border-color)' }}>
                                    <Card.Body>
                                        <p><strong>Current Application Role:</strong> {selectedUser.user_type || 'None'}</p>
                                        <Form.Group>
                                            <Form.Label>Update Application Role</Form.Label>
                                            <Form.Select
                                                value={selectedRole}
                                                onChange={(e) => setSelectedRole(e.target.value)}
                                                disabled={updateLoading}
                                                style={{ 
                                                    backgroundColor: 'var(--input-background)', 
                                                    color: 'var(--text-primary)',
                                                    borderColor: 'var(--border-color)'
                                                }}
                                            >
                                                <option value="">Select Role</option>
                                                {Object.values(appRoles).map(role => (
                                                    <option key={role} value={role}>
                                                        {role.charAt(0).toUpperCase() + role.slice(1)}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                        
                                        <div className="d-flex justify-content-end mt-3">
                                            <Button
                                                variant="primary"
                                                disabled={updateLoading || !selectedRole}
                                                onClick={handleUpdateAppRole}
                                            >
                                                {updateLoading ? (
                                                    <>
                                                        <Spinner animation="border" size="sm" className="me-2" />
                                                        Updating...
                                                    </>
                                                ) : (
                                                    'Update Role'
                                                )}
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ) : (
                                <Card style={{ backgroundColor: 'var(--background-secondary)', borderColor: 'var(--border-color)' }}>
                                    <Card.Body>
                                        <p><strong>Current Administrative Role:</strong> {formatRoleName(selectedUser.admin_role) || 'None'}</p>
                                        
                                        <Alert variant="info" className="small" style={{ backgroundColor: 'rgba(0, 123, 255, 0.1)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
                                            <strong>Note:</strong> To enable this feature, you need to add the admin role endpoints to your backend server.
                                        </Alert>
                                        
                                        <Form.Group>
                                            <Form.Label>Update Administrative Role</Form.Label>
                                            <Form.Select
                                                value={selectedRole}
                                                onChange={(e) => setSelectedRole(e.target.value)}
                                                disabled={updateLoading}
                                                style={{ 
                                                    backgroundColor: 'var(--input-background)', 
                                                    color: 'var(--text-primary)',
                                                    borderColor: 'var(--border-color)'
                                                }}
                                            >
                                                <option value="">Select Role</option>
                                                {Object.values(adminRoles).map(role => (
                                                    <option key={role} value={role}>
                                                        {formatRoleName(role)}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                        
                                        <div className="d-flex justify-content-between mt-3">
                                            {selectedUser.admin_role && (
                                                <Button
                                                    variant="outline-danger"
                                                    disabled={updateLoading}
                                                    onClick={handleRemoveAdminRole}
                                                    style={{ borderColor: '#dc3545', color: '#dc3545' }}
                                                >
                                                    {updateLoading ? (
                                                        <>
                                                            <Spinner animation="border" size="sm" className="me-2" />
                                                            Removing...
                                                        </>
                                                    ) : (
                                                        'Remove Admin Role'
                                                    )}
                                                </Button>
                                            )}
                                            <Button
                                                variant="primary"
                                                disabled={updateLoading || !selectedRole}
                                                onClick={handleUpdateAdminRole}
                                                className={selectedUser.admin_role ? '' : 'ms-auto'}
                                            >
                                                {updateLoading ? (
                                                    <>
                                                        <Spinner animation="border" size="sm" className="me-2" />
                                                        Updating...
                                                    </>
                                                ) : (
                                                    'Update Role'
                                                )}
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer style={{ backgroundColor: 'var(--card-background)', borderColor: 'var(--border-color)' }}>
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowModal(false)}
                        disabled={updateLoading}
                        style={{ backgroundColor: 'var(--background-secondary)', borderColor: 'var(--border-color)' }}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AdminUsers;
